import { supabase } from '../lib/supabase';

/**
 * Contact Service
 * Handles contact form submissions and inquiries
 */
export const contactService = {
  /**
   * Submit a contact form
   * @param {Object} formData - Contact form data
   * @returns {Promise<Object>} Created submission record
   */
  async submitContactForm(formData) {
    try {
      console.log('📝 Submitting contact form:', formData);

      // Validate required fields
      if (!formData.name || !formData.email || !formData.service || !formData.message) {
        throw new Error('Missing required fields: name, email, service, and message are required');
      }

      // Prepare submission record
      const submissionRecord = {
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        service_interest: formData.service,
        message: formData.message,
        preferred_contact_method: formData.preferredContact || 'email',
        status: 'new',
        source: 'website_contact_form'
      };

      console.log('📤 Inserting submission record:', submissionRecord);

      // Insert into database
      const { data, error } = await supabase
        .from('contact_submissions')
        .insert(submissionRecord)
        .select()
        .single();

      if (error) {
        console.error('❌ Error inserting contact submission:', error);
        throw error;
      }

      console.log('✅ Contact form submitted successfully:', data);

      // Send email notification to admin
      try {
        console.log('📧 Sending email notification to admin...');

        const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-email', {
          body: {
            type: 'contact_form',
            data: {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              service: formData.service,
              message: formData.message,
              preferredContact: formData.preferredContact
            }
          }
        });

        if (emailError) {
          console.error('❌ Error sending email:', emailError);
          console.warn('⚠️ Contact form saved but email notification failed');
          console.warn('💡 See EMAIL_WHATSAPP_INTEGRATION_SETUP.md for setup instructions');
        } else if (emailResult?.success) {
          console.log('✅ Email notification sent successfully:', emailResult.emailId);
        } else {
          console.warn('⚠️ Email send function returned error:', emailResult?.error);
        }
      } catch (emailException) {
        console.error('❌ Exception sending email:', emailException);
        console.warn('⚠️ This is expected if the Edge Function is not deployed yet.');
        console.warn('💡 To deploy: npx supabase functions deploy send-email');
      }

      // Send WhatsApp notification to admin if phone number is provided
      if (formData.phone && formData.preferredContact === 'whatsapp') {
        try {
          console.log('📱 Sending WhatsApp notification to admin...');

          const whatsappMessage = `🔔 *New Contact Form Submission*\n\n` +
            `*Name:* ${formData.name}\n` +
            `*Email:* ${formData.email}\n` +
            `*Phone:* ${formData.phone}\n` +
            `*Service:* ${formData.service}\n` +
            `*Preferred Contact:* ${formData.preferredContact}\n\n` +
            `*Message:*\n${formData.message}\n\n` +
            `_Submitted at: ${new Date().toLocaleString()}_`;

          const { data: whatsappResult, error: whatsappError } = await supabase.functions.invoke('send-whatsapp', {
            body: {
              to: '7842202255', // Admin phone number
              message: whatsappMessage,
              messageId: data.id
            }
          });

          if (whatsappError) {
            console.error('❌ Error sending WhatsApp:', whatsappError);
            console.warn('⚠️ Contact form saved but WhatsApp notification failed');
          } else if (whatsappResult?.success) {
            console.log('✅ WhatsApp notification sent successfully:', whatsappResult.messageSid);
          } else {
            console.warn('⚠️ WhatsApp send function returned error:', whatsappResult?.error);
          }
        } catch (whatsappException) {
          console.error('❌ Exception sending WhatsApp:', whatsappException);
          console.warn('⚠️ This is expected if the Edge Function is not deployed yet.');
          console.warn('💡 To deploy: npx supabase functions deploy send-whatsapp');
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('❌ Exception in submitContactForm:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Get all contact submissions (admin only)
   * @param {Object} filters - Optional filters (status, dateRange, etc.)
   * @returns {Promise<Array>} List of submissions
   */
  async getSubmissions(filters = {}) {
    try {
      let query = supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching submissions:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Update submission status
   * @param {string} submissionId - Submission ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated submission
   */
  async updateSubmission(submissionId, updates) {
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .update(updates)
        .eq('id', submissionId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating submission:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Mark submission as responded
   * @param {string} submissionId - Submission ID
   * @param {string} responseNotes - Response notes
   * @returns {Promise<Object>} Updated submission
   */
  async markAsResponded(submissionId, responseNotes) {
    try {
      const updates = {
        status: 'responded',
        response_notes: responseNotes,
        responded_at: new Date().toISOString()
      };

      return await this.updateSubmission(submissionId, updates);
    } catch (error) {
      console.error('Error marking submission as responded:', error);
      return { data: null, error: error.message };
    }
  }
};

export default contactService;

